import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  GraduationCap, 
  Sparkles, 
  Building, 
  Award, 
  BookOpen, 
  FileText, 
  Phone, 
  MapPin,
  Globe,
  Clock,
  Users,
  Star,
  ChevronDown,
  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Détection du scroll pour l'effet de transparence
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { 
      name: 'Accueil', 
      href: '/',
      icon: Building
    },
    { 
      name: 'Documents', 
      href: '/documents',
      icon: FileText
    }
  ];

  const formations = [
    { name: 'Cursus Scolaire', description: '6ème à Terminale', href: '#formations' },
    { name: 'Cours Week-end', description: 'Programmes intensifs', href: '#formations' },
    { name: 'Formations Sur Mesure', description: 'Personnalisées', href: '#formations' }
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleMenuClick = () => {
    setIsMenuOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Fermer le menu mobile d'abord
      setIsMenuOpen(false);
      setIsDropdownOpen(false);
      
      // Attendre un peu que le menu se ferme, puis scroller
      setTimeout(() => {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 300);
    }
  };

  const handleNavigation = (path: string) => {
    // Fermer les menus d'abord
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    
    // Attendre un peu que les menus se ferment
    setTimeout(() => {
      if (path === '/documents') {
        navigate('/documents');
      } else if (path === 'formations') {
        const element = document.getElementById('formations');
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }, 300);
  };

  const handleMobileNavigation = (path: string) => {
    // Fermer les menus d'abord
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    
    // Navigation immédiate
    if (path === '/') {
      navigate('/');
      // Scroll vers le haut de la page d'accueil
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else if (path === '/documents') {
      navigate('/documents');
      // Scroll vers le haut de la page documents
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else if (path === 'formations') {
      // Si on est déjà sur la page d'accueil, scroller vers la section
      if (location.pathname === '/') {
        setTimeout(() => {
          const element = document.getElementById('formations');
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 100);
      } else {
        // Si on est sur une autre page, naviguer vers l'accueil puis scroller
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById('formations');
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 500);
      }
    }
  };

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-2xl border-b border-gray-200/50' 
          : 'bg-white/80 backdrop-blur-lg shadow-lg'
      }`}
    >
      {/* Barre supérieure avec informations de contact */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between py-2 sm:h-10 text-xs">
            <div className="flex flex-wrap items-center justify-center sm:justify-start space-x-4 sm:space-x-6 mb-2 sm:mb-0">
              <div className="flex items-center space-x-2">
                <Phone className="h-3 w-3 text-blue-200" />
                <span className="font-medium">+221 33 894 37 52</span>
              </div>
              <div className="hidden sm:flex items-center space-x-2">
                <MapPin className="h-3 w-3 text-blue-200" />
                <a
                  href="https://maps.app.goo.gl/iboaayWNsjRq1poh9?g_st=iwb"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline underline-offset-2 decoration-white/40 hover:decoration-white"
                >
                  Cité Alioune SOW de Golf, Dakar
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="h-3 w-3 text-blue-200" />
                <span className="font-medium">Lun-Ven: 8h-16h</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Star className="h-3 w-3 text-yellow-400" />
                <span className="font-medium">Curiosité-Réflexion-Ambition-Créativité</span>
              </div>
              <div className="hidden sm:flex items-center space-x-2">
                <Users className="h-3 w-3 text-blue-200" />
                <span className="font-medium">200+ élèves</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation principale */}
      <div className="bg-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-12 lg:h-16">
          {/* Logo Institutionnel Ultra-Moderne */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="group"
          >
            <Link to="/" className="flex items-center space-x-3 -ml-2 sm:-ml-3 lg:-ml-4">
              <div className="relative">
                <img
                  src="/images/logo.png?v=1"
                  alt="LES CRAC ACADEMY"
                  className="w-12 h-12 lg:w-14 lg:h-14 object-contain"
                  loading="eager"
                  decoding="async"
                />
              </div>
             {/*  <div>
                <div className="flex items-baseline space-x-1">
                  <span className="text-xl lg:text-2xl xl:text-3xl font-black bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                    LES CRAC
                  </span>
                 {  <span className="text-lg lg:text-xl xl:text-2xl font-black bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                    ACADEMY
                  </span> }
                </div>
               { <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs lg:text-sm font-semibold text-gray-600 tracking-wider uppercase">
                    Excellence Scientifique
                  </span>
                </div> }
              </div> */}
            </Link>
          </motion.div>

          {/* Navigation Desktop Ultra-Moderne */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <motion.div
                key={item.name}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative"
              >
                <button
                  onClick={() => {
                    if (item.href === '/') {
                      navigate('/');
                      // Scroll vers le haut de la page d'accueil
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 100);
                    } else if (item.href === '/documents') {
                      navigate('/documents');
                      // Scroll vers le haut de la page documents
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 100);
                    }
                  }}
                  className={`relative px-5 py-2 rounded-xl text-sm font-bold transition-all duration-300 flex items-center space-x-2 group ${
                    isActive(item.href)
                      ? 'text-blue-600 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg border border-blue-200'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 border border-transparent hover:border-blue-200'
                  }`}
                >
                  <item.icon className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  <span>{item.name}</span>
                  {isActive(item.href) && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl -z-10"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </button>
              </motion.div>
            ))}

            {/* Dropdown Formations */}
            <motion.div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  // Si on est déjà sur la page d'accueil, scroller vers la section
                  if (location.pathname === '/') {
                    const element = document.getElementById('formations');
                    if (element) {
                      element.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  } else {
                    // Si on est sur une autre page, naviguer vers l'accueil puis scroller
                    navigate('/');
                    setTimeout(() => {
                      const element = document.getElementById('formations');
                      if (element) {
                        element.scrollIntoView({ 
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }
                    }, 500);
                  }
                }}
                className="relative px-5 py-2 rounded-xl text-sm font-bold transition-all duration-300 flex items-center space-x-2 group text-gray-700 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 border border-transparent hover:border-blue-200"
              >
                <BookOpen className="h-4 w-4 group-hover:scale-110 transition-transform" />
                <span>Formations</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </motion.button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="text-sm font-bold text-gray-900 mb-3 px-2">Nos Formations</div>
                      <div className="space-y-2">
                        {formations.map((formation, index) => (
                          <motion.div
                            key={formation.name}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <button
                              onClick={() => {
                                // Si on est déjà sur la page d'accueil, scroller vers la section
                                if (location.pathname === '/') {
                                  const element = document.getElementById('formations');
                                  if (element) {
                                    element.scrollIntoView({ 
                                      behavior: 'smooth',
                                      block: 'start'
                                    });
                                  }
                                } else {
                                  // Si on est sur une autre page, naviguer vers l'accueil puis scroller
                                  navigate('/');
                                  setTimeout(() => {
                                    const element = document.getElementById('formations');
                                    if (element) {
                                      element.scrollIntoView({ 
                                        behavior: 'smooth',
                                        block: 'start'
                                      });
                                    }
                                  }, 500);
                                }
                              }}
                              className="w-full text-left p-3 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                    {formation.name}
                                  </div>
                                  <div className="text-sm text-gray-600">{formation.description}</div>
                                </div>
                                <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                              </div>
                            </button>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </nav>

          {/* Informations Institutionnelles Desktop */}
          <div className="hidden xl:flex items-center space-x-6">
            <div className="flex items-center space-x-3 px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
              <Award className="h-4 w-4 text-green-600" />
              <span className="text-sm font-semibold text-green-700">100% au BFEM</span>
            </div>
            <div className="flex items-center space-x-3 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
              <Building className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-700">Établissement d'Excellence</span>
            </div>
          </div>

          {/* Mobile menu button Ultra-Moderne */}
          <div className="lg:hidden">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative w-10 h-10 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center border-2 border-blue-400/30"
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-6 w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation Ultra-Moderne */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl mt-2 p-6 shadow-2xl border border-gray-200/50">
                <div className="space-y-4">
                  {navigation.map((item) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <button
                        onClick={() => handleMobileNavigation(item.href)}
                        className={`w-full text-left px-6 py-4 rounded-xl text-sm font-bold transition-all duration-300 flex items-center space-x-3 ${
                          isActive(item.href)
                            ? 'text-blue-600 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg border border-blue-200'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 border border-transparent hover:border-blue-200'
                        }`}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.name}</span>
                      </button>
                    </motion.div>
                  ))}
                  
                  {/* Section Formations Mobile avec Dropdown */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-2"
                  >
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full text-left px-6 py-4 rounded-xl text-sm font-bold transition-all duration-300 flex items-center justify-between text-gray-700 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 border border-transparent hover:border-blue-200"
                    >
                      <div className="flex items-center space-x-3">
                        <BookOpen className="h-5 w-5" />
                        <span>Formations</span>
                      </div>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-6 space-y-2">
                            {formations.map((formation, index) => (
                              <motion.div
                                key={formation.name}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                              >
                                <button
                                  onClick={() => handleMobileNavigation('formations')}
                                  className="w-full text-left px-4 py-3 rounded-lg text-sm transition-all duration-200 flex items-center justify-between text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                                >
                                  <div>
                                    <div className="font-semibold">{formation.name}</div>
                                    <div className="text-xs text-gray-500">{formation.description}</div>
                                  </div>
                                  <ArrowRight className="h-3 w-3 text-gray-400" />
                                </button>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>

                {/* Informations de contact mobiles */}
                <div className="mt-6 pt-6 border-t border-gray-200/50">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <Award className="h-4 w-4 text-green-600" />
                      <span className="font-medium">100% au BFEM</span>
                    </div>
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <Building className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">Établissement d'Excellence</span>
                    </div>
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <Users className="h-4 w-4 text-purple-600" />
                      <span className="font-medium">150+ Élèves actifs</span>
                    </div>
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="font-medium">10 ans d'excellence</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>
    </motion.header>
  );
};

export default Header;